import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box } from '@mui/system';
import {
  TableContainer,
  IconButton,
  TextField,
  TableBody,
  TableCell,
  TableRow,
  Table,
  Button,
  Typography,
  Paper,
  Tooltip,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import { Link } from 'react-router-dom';

import {
  fetchDoctorCategories,
  deleteDoctorCategory,
  createOrUpdateDoctorCategory,
  reorderDoctorCategories,
} from '../http/categoryAPI';
import { DOCTORS_ROUTE } from '../utils/consts';
import { getImageUrl } from '../helpers/getImageUrl';

const DoctorCategories = () => {
  const [categories, setCategories] = useState([]);
  const [name, setName] = useState('');
  const [doctorsMenuAnchorEl, setDoctorsMenuAnchorEl] = useState(null);
  const [doctorsMenuCategoryId, setDoctorsMenuCategoryId] = useState(null);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = () => {
    fetchDoctorCategories().then((data) => {
      setCategories(data);
    });
  };

  const addCategory = () => {
    if (!name.trim()) return;
    
    createOrUpdateDoctorCategory({ name }).then((data) => {
      if (!data.error) {
        setCategories([...categories, data]);
        setName('');
      }
    });
  };

  const removeCategory = (id) => {
    deleteDoctorCategory(id).then((data) => {
      if (!data.error) {
        setCategories((current) =>
          current.filter((category) => category.id !== id)
        );
      }
    });
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(categories);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setCategories(items);

    // Обновляем индексы на сервере
    const categoriesWithIndex = items.map((cat, index) => ({
      id: cat.id,
      index: index,
    }));

    reorderDoctorCategories(categoriesWithIndex).catch(() => {
      // В случае ошибки перезагружаем
      loadCategories();
    });
  };

  const closeDoctorsMenu = () => {
    setDoctorsMenuAnchorEl(null);
  };

  const openDoctorsMenu = (categoryId, anchorEl) => {
    setDoctorsMenuCategoryId(categoryId);
    setDoctorsMenuAnchorEl(anchorEl);
  };

  const handleDoctorsCountClick = (event, categoryId) => {
    event.stopPropagation();
    if (doctorsMenuAnchorEl && doctorsMenuCategoryId === categoryId) {
      closeDoctorsMenu();
      return;
    }
    openDoctorsMenu(categoryId, event.currentTarget);
  };

  const activeCategory = categories.find(
    (category) => category.id === doctorsMenuCategoryId
  );
  const isDoctorsMenuOpen = Boolean(doctorsMenuAnchorEl);

  return (
    <Box sx={{ mt: 0 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Вы можете разделить врачей на категории. Они будут отображаться в боте при нажатии кнопки «Врачи».
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          value={name}
          onChange={(e) => setName(e.target.value)}
          label="Название категории"
          variant="outlined"
          size="small"
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              addCategory();
            }
          }}
          fullWidth
        />
        <Button
          variant="contained"
          onClick={addCategory}
          startIcon={<AddCircleOutlineIcon />}
          disabled={!name.trim()}
        >
          Добавить
        </Button>
      </Box>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="categories">
          {(provided) => (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                  {categories.map((category, index) => (
                    <Draggable
                      key={category.id}
                      draggableId={category.id}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <TableRow
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          sx={{
                            backgroundColor: snapshot.isDragging
                              ? 'action.hover'
                              : 'inherit',
                          }}
                        >
                          <TableCell width={50}>
                            <Tooltip
                              title="Зажмите и перетащите, чтобы изменить порядок категорий врачей в боте"
                              arrow
                            >
                              <Box {...provided.dragHandleProps} sx={{ cursor: 'grab' }}>
                                <DragIndicatorIcon color="action" />
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1">
                              {category.name}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip
                              title={
                                category.doctors?.length
                                  ? 'Нажмите, чтобы посмотреть врачей'
                                  : 'В этой категории пока нет врачей'
                              }
                              arrow
                            >
                              <Button
                                variant="text"
                                size="small"
                                onClick={(event) =>
                                  handleDoctorsCountClick(event, category.id)
                                }
                                sx={{
                                  minWidth: 0,
                                  px: 0.5,
                                  color: 'text.secondary',
                                  textTransform: 'none',
                                  textDecoration: 'underline',
                                  textDecorationStyle: 'dashed',
                                  textUnderlineOffset: '3px',
                                }}
                              >
                                {category.doctors?.length || 0} врачей
                              </Button>
                            </Tooltip>
                          </TableCell>
                          <TableCell width={50}>
                            <IconButton
                              onClick={() => removeCategory(category.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Droppable>
      </DragDropContext>

      {categories.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          Нет категорий. Добавьте первую категорию.
        </Typography>
      )}

      <Popover
        open={isDoctorsMenuOpen}
        anchorEl={doctorsMenuAnchorEl}
        onClose={closeDoctorsMenu}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableRestoreFocus
        TransitionProps={{
          onExited: () => setDoctorsMenuCategoryId(null),
        }}
        PaperProps={{
          sx: {
            width: 340,
            maxWidth: 'calc(100vw - 32px)',
            p: 1,
          },
        }}
      >
        <Typography variant="subtitle2" sx={{ px: 1, pb: 1 }}>
          Врачи в категории «{activeCategory?.name || 'Без названия'}»
        </Typography>
        <Divider />

        {activeCategory?.doctors?.length ? (
          <List dense sx={{ maxHeight: 280, overflowY: 'auto', py: 0.5 }}>
            {activeCategory.doctors.map((doctor) => (
              <ListItemButton
                key={doctor.id}
                component={Link}
                to={`${DOCTORS_ROUTE}/${doctor.id}`}
                onClick={closeDoctorsMenu}
                sx={{
                  alignItems: 'stretch',
                  py: 1,
                  px: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  transition: 'background-color 120ms ease',
                  '&:last-of-type': {
                    borderBottom: 'none',
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <Box
                  component="img"
                  src={getImageUrl(doctor.image) || getImageUrl('undefined.jpg')}
                  alt={doctor.name || 'Врач'}
                  sx={{
                    width: 72,
                    minWidth: 72,
                    height: 72,
                    borderRadius: 1,
                    objectFit: 'cover',
                    mr: 1.25,
                    alignSelf: 'center',
                  }}
                />
                <ListItemText
                  primary={doctor.name || 'Без названия'}
                  secondary={doctor.description || 'Описание отсутствует'}
                  primaryTypographyProps={{
                    variant: 'body1',
                    sx: {
                      fontWeight: 600,
                      lineHeight: 1.3,
                      mb: 0.3,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    },
                  }}
                  secondaryTypographyProps={{
                    variant: 'body2',
                    color: 'text.secondary',
                    sx: {
                      lineHeight: 1.35,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    },
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
            В этой категории пока нет врачей.
          </Typography>
        )}
      </Popover>
    </Box>
  );
};

export default DoctorCategories;
